import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ImageViewerProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const ZOOM_LEVELS = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5];

const ImageViewer = ({ src, alt, onClose }: ImageViewerProps) => {
  const [zoomIndex, setZoomIndex] = useState(0);
  const zoom = ZOOM_LEVELS[zoomIndex];

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleZoomIn = () =>
      setZoomIndex((prev) => Math.min(prev + 1, ZOOM_LEVELS.length - 1));
  const handleZoomOut = () =>
      setZoomIndex((prev) => Math.max(prev - 1, 0));

  return (
      <div
          className="fixed inset-0 z-[100] bg-black/95 overflow-auto cursor-default"
          onClick={onClose}
      >
        {/* Close button */}
        <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-4 right-4 text-white hover:text-white/80 transition-colors z-[101]"
            aria-label="Close viewer"
        >
          <X className="h-8 w-8" />
        </button>

        {/* Zoom controls */}
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-[101]">
          <button
              onClick={(e) => {
                e.stopPropagation();
                handleZoomOut();
              }}
              disabled={zoomIndex === 0}
              className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-lg transition-colors disabled:opacity-30"
              aria-label="Zoom out"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <span className="bg-white/10 text-white px-4 py-3 rounded-lg min-w-[80px] text-center">
          {Math.round(zoom * 100)}%
        </span>
          <button
              onClick={(e) => {
                e.stopPropagation();
                handleZoomIn();
              }}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1}
              className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-lg transition-colors disabled:opacity-30"
              aria-label="Zoom in"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
        </div>

        {/* Image container */}
        <div
            className="w-full h-full flex justify-center items-start p-8"
            onClick={(e) => e.stopPropagation()}
        >
          <img
              src={src}
              alt={alt}
              className="transition-transform duration-200 object-contain cursor-default"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
              }}
          />
        </div>
      </div>
  );
};

export default ImageViewer;

